import React, { useReducer, useContext, useEffect } from 'react';
// Reducer
import AppReducer from './AppReducer';

const AppStateContext = React.createContext(/** @type {Store} */ ({}));
const AppDispatchContext = React
  .createContext(/** @type {React.Dispatch<{ type: AppContextTypes; payload: any; }>} */ ({}));

const localStorageUserRaw = window.localStorage.getItem('user');
const localStorageUser = localStorageUserRaw ? JSON.parse(localStorageUserRaw) : null;
const localStorageAuthRaw = window.localStorage.getItem('auth');
const localStorageAuth = localStorageAuthRaw ? JSON.parse(localStorageAuthRaw) : null;

/**
 * Initial state for the global application context.
 * @type {Store}
 */
const initStore = {
  auth: localStorageAuth,
  user: localStorageUser,
};

/**
 * Set the token in local storage.
 * @param {string} key
 * @param {any} newValue
 */
const setPersistantState = (key, newValue) => {
  if (newValue) {
    window.localStorage.setItem(key, newValue);
  } else {
    window.localStorage.removeItem(key);
  }
};

/**
 * Provider for the global application context.
 * This provider wraps the application in a context provider, allowing components to
 * access the global state and dispatch function.
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @returns {JSX.Element}
 */
function AppProvider({ children }) {
  const [state, dispatch] = useReducer(AppReducer, initStore);

  useEffect(() => {
    setPersistantState('auth', JSON.stringify(state.auth));
  }, [state]);
  return (
    <AppStateContext.Provider value={state}>
      <AppDispatchContext.Provider value={dispatch}>
        {children}
      </AppDispatchContext.Provider>
    </AppStateContext.Provider>
  );
}

/**
 * Custom hook for accessing the global application context and dispatch function.
 *
 * This hook simplifies the process of accessing the global state and dispatch function
 * from the application's context. It returns an array containing the current state
 * and the dispatch function, allowing components to read from the global state and
 * dispatch actions to modify it.
 * @returns {[Store, AppContextDispatch]}
 * state object and the second element is the dispatch function to update the state.
 */
const useAppContext = () => [useContext(AppStateContext), useContext(AppDispatchContext)];

export {
  AppProvider,
  AppStateContext,
  AppDispatchContext,
  useAppContext,
};
